import { GetSignalingChannelEndpointCommand, KinesisVideoClient, type ResourceEndpointListItem } from '@aws-sdk/client-kinesis-video';
import { GetIceServerConfigCommand, KinesisVideoSignalingClient, type IceServer } from '@aws-sdk/client-kinesis-video-signaling';
import { Role, SignalingClient } from 'amazon-kinesis-video-streams-webrtc';
import type { TemporaryCredentials } from '@/api/types';
import type { DirectoryEvent } from './RobotDirectoryClient';
import { RobotRpcClient } from './RobotRpcClient';
import type { RpcCallOptions, RpcService } from './rpcTypes';
import {
  NOOP_TELEMETRY_REPORTER,
  reportTelemetrySafely,
  type TelemetryEventType,
  type TelemetryReporter,
} from '@/telemetry';

export type RobotConnectionState = 'idle' | 'preparing' | 'connecting' | 'connected' | 'reconnecting' | 'offline' | 'failed';

export type RobotConnectionConfig = {
  robotId: string;
  channelArn: string;
  channelName: string;
  region: string;
  credentials: TemporaryCredentials;
  connectionSessionId: string;
  connectionRequestId: string;
  directoryGeneration: number;
};

export type RobotConnectionSnapshot = {
  robotId: string;
  state: RobotConnectionState;
  generation: number;
  lastError: string | null;
  remoteStream: MediaStream | null;
  connectedAt: string | null;
};

type SnapshotListener = () => void;
export type RobotButtonEvent = {
  code: number;
  kind: number;
  angles?: Record<string, number | null>;
};
type RobotButtonListener = (event: RobotButtonEvent) => void;
type ReconnectConfigProvider = (current: RobotConnectionConfig) => RobotConnectionConfig;
export const CONNECT_TIMEOUT_MS = 20_000;
export const DISCONNECT_GRACE_MS = 8_000;
const RECONNECT_DELAY_MS = 2500;

type ActiveConnectAttempt = {
  generation: number;
  signal: AbortSignal;
  cancellation: Promise<never>;
  cancel: (error: Error) => void;
  complete: () => void;
};

type IceCandidatePeer = Pick<RTCPeerConnection, 'remoteDescription' | 'addIceCandidate'>;

export class PendingRemoteIceCandidates {
  private candidates: RTCIceCandidateInit[] = [];

  async add(peer: IceCandidatePeer, candidate: RTCIceCandidateInit) {
    if (!peer.remoteDescription) {
      this.candidates.push(candidate);
      return;
    }
    await peer.addIceCandidate(candidate);
  }

  async flush(peer: IceCandidatePeer, shouldContinue: () => boolean = () => true) {
    const candidates = this.candidates.splice(0);
    for (const candidate of candidates) {
      if (!shouldContinue()) return;
      await peer.addIceCandidate(candidate);
    }
  }
}

const endpointsByProtocol = (items?: ResourceEndpointListItem[]) => (items ?? []).reduce<Record<string, string>>((result, item) => {
  if (item.Protocol && item.ResourceEndpoint) result[item.Protocol] = item.ResourceEndpoint;
  return result;
}, {});

const toIceServers = (items: IceServer[] | undefined, region: string): RTCIceServer[] => [
  { urls: `stun:stun.kinesisvideo.${region}.amazonaws.com:443` },
  ...(items ?? []).filter((item) => item.Uris?.length).map((item) => ({
    urls: item.Uris!,
    username: item.Username,
    credential: item.Password,
  })),
];

export class RobotConnectionSession {
  readonly rpc: RobotRpcClient;
  private config: RobotConnectionConfig | null = null;
  private peer: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private signaling: SignalingClient | null = null;
  private activeConnect: ActiveConnectAttempt | null = null;
  private disconnectGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private manualClose = false;
  private latestRobotStatusAt = Number.NEGATIVE_INFINITY;
  private listeners = new Set<SnapshotListener>();
  private buttonListeners = new Set<RobotButtonListener>();
  private snapshot: RobotConnectionSnapshot;

  constructor(
    readonly robotId: string,
    private readonly telemetryReporter: TelemetryReporter = NOOP_TELEMETRY_REPORTER,
    private readonly reconnectConfigProvider: ReconnectConfigProvider = (current) => ({
      ...current,
      connectionRequestId: crypto.randomUUID(),
      directoryGeneration: current.directoryGeneration + 1,
    }),
  ) {
    this.rpc = new RobotRpcClient(robotId, telemetryReporter, () => this.config?.connectionSessionId);
    this.snapshot = { robotId, state: 'idle', generation: 0, lastError: null, remoteStream: null, connectedAt: null };
  }

  subscribe(listener: SnapshotListener) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  subscribeRobotButton(listener: RobotButtonListener) {
    this.buttonListeners.add(listener);
    return () => { this.buttonListeners.delete(listener); };
  }

  getSnapshot = () => this.snapshot;

  isConnected() {
    return this.snapshot.state === 'connected' && this.dataChannel?.readyState === 'open';
  }

  updateConfig(config: RobotConnectionConfig): boolean {
    if (
      this.disposed
      || this.manualClose
      || !this.isConnected()
      || this.config?.channelArn !== config.channelArn
      || this.config.region !== config.region
      || config.robotId !== this.robotId
    ) return false;
    this.config = config;
    return true;
  }

  async connect(config?: RobotConnectionConfig): Promise<void> {
    if (this.disposed) throw new Error('Session is disposed');
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const nextConfig = config ?? this.reconnectConfigProvider(this.requireConfig());
    this.cleanupTransport(new DOMException('Connection superseded by a newer attempt', 'AbortError'));
    this.config = nextConfig;
    this.manualClose = false;
    const generation = this.snapshot.generation + 1;
    const startedAt = performance.now();
    this.update({ state: this.snapshot.generation === 0 ? 'connecting' : 'reconnecting', generation, lastError: null, remoteStream: null, connectedAt: null });
    const attempt = this.createConnectAttempt(generation);
    this.activeConnect = attempt;

    try {
      await Promise.race([
        this.openTransport(nextConfig, generation, startedAt, attempt.signal),
        attempt.cancellation,
      ]);
    } catch (error) {
      if (
        generation !== this.snapshot.generation
        || this.disposed
        || this.manualClose
        || (error instanceof DOMException && error.name === 'AbortError')
      ) throw error;
      const message = error instanceof Error ? error.message : String(error);
      this.update({ state: 'failed', lastError: message });
      this.cleanupTransport(error instanceof Error ? error : new Error(message));
      this.reportWebRtc('webrtc.failed', nextConfig, {
        durationMs: performance.now() - startedAt,
        errorCode: error instanceof Error && error.message === 'WebRTC connection timed out'
          ? 'WEBRTC_TIMEOUT'
          : 'WEBRTC_CONNECT_FAILED',
      });
      this.scheduleReconnect();
      throw error;
    } finally {
      if (this.activeConnect === attempt) {
        attempt.complete();
        this.activeConnect = null;
      }
    }
  }

  private async openTransport(
    config: RobotConnectionConfig,
    generation: number,
    startedAt: number,
    signal: AbortSignal,
  ): Promise<void> {
    const credentials = {
      accessKeyId: config.credentials.accessKeyId,
      secretAccessKey: config.credentials.secretAccessKey,
      sessionToken: config.credentials.sessionToken,
    };
    const videoClient = new KinesisVideoClient({ region: config.region, credentials });
    let signalingApi: KinesisVideoSignalingClient | null = null;
    let endpoints: Record<string, string> = {};
    let iceServers: IceServer[] | undefined;
    try {
      const endpointResponse = await videoClient.send(new GetSignalingChannelEndpointCommand({
        ChannelARN: config.channelArn,
        SingleMasterChannelEndpointConfiguration: { Protocols: ['WSS', 'HTTPS'], Role: 'VIEWER' },
      }), { abortSignal: signal });
      this.assertCurrentGeneration(generation, signal);
      endpoints = endpointsByProtocol(endpointResponse.ResourceEndpointList);
      if (!endpoints.WSS || !endpoints.HTTPS) throw new Error('Kinesis signaling endpoint is incomplete');
      signalingApi = new KinesisVideoSignalingClient({ region: config.region, endpoint: endpoints.HTTPS, credentials });
      const iceResponse = await signalingApi.send(
        new GetIceServerConfigCommand({ ChannelARN: config.channelArn }),
        { abortSignal: signal },
      );
      iceServers = iceResponse.IceServerList;
      this.assertCurrentGeneration(generation, signal);
    } finally {
      signalingApi?.destroy();
      videoClient.destroy();
    }

    const peer = new RTCPeerConnection({ iceServers: toIceServers(iceServers, config.region) });
    this.peer = peer;
    const stream = new MediaStream();
    const signaling = new SignalingClient({
      channelARN: config.channelArn,
      channelEndpoint: endpoints.WSS,
      clientId: `filo-${crypto.randomUUID()}`,
      credentials,
      region: config.region,
      role: Role.VIEWER,
    });
    this.signaling = signaling;
    const pendingRemoteIce = new PendingRemoteIceCandidates();

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let connected = false;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', handleAbort);
        if (error) reject(error);
        else resolve();
      };
      const handleAbort = () => finish(signal.reason instanceof Error
        ? signal.reason
        : new DOMException('Connection attempt aborted', 'AbortError'));
      signal.addEventListener('abort', handleAbort, { once: true });
      if (signal.aborted) {
        handleAbort();
        return;
      }
      const isCurrent = () => (
        !this.disposed
        && !signal.aborted
        && generation === this.snapshot.generation
        && this.peer === peer
      );

      peer.onicecandidate = (event) => {
        if (isCurrent() && event.candidate) signaling.sendIceCandidate(event.candidate);
      };
      peer.ontrack = (event) => {
        if (!isCurrent()) return;
        stream.addTrack(event.track);
        event.track.onended = () => stream.removeTrack(event.track);
        this.update({ remoteStream: stream });
      };
      peer.onconnectionstatechange = () => {
        if (!isCurrent()) return;
        if (peer.connectionState === 'failed' || peer.connectionState === 'closed') {
          this.clearDisconnectGrace();
          if (!settled) {
            finish(new Error('WebRTC connection closed'));
            return;
          }
          if (!connected) return;
          this.handleUnexpectedClose('WebRTC connection closed', 'WEBRTC_CONNECTION_CLOSED');
        } else if (peer.connectionState === 'disconnected') {
          if (!settled) {
            this.scheduleDisconnectGrace(peer, generation, () => {
              finish(new Error('WebRTC connection disconnected'));
            });
            return;
          }
          if (!connected) return;
          this.scheduleDisconnectGrace(peer, generation, () => {
            this.handleUnexpectedClose('WebRTC connection disconnected', 'WEBRTC_CONNECTION_DISCONNECTED');
          });
        } else if (peer.connectionState === 'connected') {
          this.clearDisconnectGrace();
        }
      };

      const channel = peer.createDataChannel('robot_channel');
      this.dataChannel = channel;
      channel.onopen = () => {
        if (!isCurrent() || this.dataChannel !== channel || settled) return;
        connected = true;
        this.rpc.setDataChannel(channel, generation);
        this.update({ state: 'connected', lastError: null, connectedAt: new Date().toISOString() });
        this.reportWebRtc('webrtc.connected', config, { durationMs: performance.now() - startedAt });
        finish();
      };
      channel.onmessage = (event) => {
        if (!isCurrent()) return;
        const handled = this.rpc.handleMessage(event.data, generation);
        if (handled) return;
        try {
          const message = JSON.parse(String(event.data)) as { action?: string; data?: RobotButtonEvent };
          if (message.action !== 'onButton' || !message.data) return;
          if (typeof message.data.code !== 'number' || typeof message.data.kind !== 'number') return;
          this.buttonListeners.forEach((listener) => listener(message.data!));
        } catch {
          // Ignore malformed unsolicited messages.
        }
      };
      channel.onerror = () => { if (isCurrent()) this.update({ lastError: 'DataChannelでエラーが発生しました' }); };
      channel.onclose = () => {
        if (!isCurrent()) return;
        if (!settled) {
          finish(new Error('DataChannel closed before connection completed'));
          return;
        }
        if (!connected) return;
        this.handleUnexpectedClose('DataChannel closed', 'DATA_CHANNEL_CLOSED');
      };

      peer.addTransceiver('audio', { direction: 'recvonly' });
      peer.addTransceiver('video', { direction: 'recvonly' });
      signaling.on('open', async () => {
        if (!isCurrent()) return;
        try {
          const offer = await peer.createOffer();
          if (!isCurrent()) return;
          const description = new RTCSessionDescription(offer);
          await peer.setLocalDescription(description);
          if (!isCurrent()) return;
          signaling.sendSdpOffer(description);
        } catch (error) {
          if (isCurrent()) finish(error);
        }
      });
      signaling.on('sdpAnswer', async (answer: unknown) => {
        if (!isCurrent()) return;
        try {
          await peer.setRemoteDescription(answer as RTCSessionDescriptionInit);
          if (!isCurrent()) return;
          await pendingRemoteIce.flush(peer, isCurrent);
        } catch (error) {
          if (isCurrent()) finish(error);
        }
      });
      signaling.on('iceCandidate', async (candidate: unknown) => {
        if (!isCurrent()) return;
        const remoteCandidate = candidate as RTCIceCandidateInit;
        try {
          await pendingRemoteIce.add(peer, remoteCandidate);
        } catch (error) {
          if (isCurrent()) this.update({ lastError: String(error) });
        }
      });
      signaling.on('error', (error: unknown) => {
        if (!isCurrent()) return;
        if (!settled) {
          finish(error);
        }
      });
      signaling.on('close', () => {
        if (!isCurrent()) return;
        if (!settled) {
          finish(new Error('Kinesis signaling closed'));
        }
      });
      signaling.open();
    });
  }

  call<T = unknown>(service: RpcService, data?: unknown, options?: RpcCallOptions) {
    return this.rpc.call<T>(service, data, options);
  }

  requestCamera(enabled: boolean, options?: RpcCallOptions) {
    return this.call(enabled ? 'cameraOn' : 'cameraOff', undefined, options);
  }

  handleDirectoryEvent(event: DirectoryEvent) {
    const config = this.config;
    if (!config || event.robotId !== this.robotId) return false;
    if (event.connectionRequestId && event.connectionRequestId !== config.connectionRequestId) return false;
    if (typeof event.generation === 'number' && event.generation !== config.directoryGeneration) return false;
    if (event.type === 'robotStatus') {
      const occurredAt = typeof event.occurredAt === 'string' ? Date.parse(event.occurredAt) : Number.NaN;
      if (!Number.isFinite(occurredAt) || occurredAt <= this.latestRobotStatusAt) return false;
      this.latestRobotStatusAt = occurredAt;
      if (event.online === true && this.snapshot.state === 'offline' && !this.manualClose && !this.disposed) {
        this.update({ state: 'reconnecting', lastError: null });
        this.scheduleReconnect();
      }
    }
    if (
      event.type === 'prepareFailed'
      || (event.type === 'robotStatus' && event.online === false && !this.isConnected())
    ) {
      const eventType: TelemetryEventType = this.isConnected() ? 'webrtc.disconnected' : 'webrtc.failed';
      this.reportWebRtc(eventType, config, {
        durationMs: this.connectedDurationMs(),
        errorCode: event.type === 'prepareFailed' ? 'PREPARE_FAILED' : 'ROBOT_OFFLINE',
      });
      this.update({ state: 'offline', lastError: typeof event.message === 'string' ? event.message : 'ロボットはオフラインです' });
      this.cleanupTransport();
    }
    return true;
  }

  async disconnect(reason = 'manual disconnect') {
    const config = this.config;
    const wasConnected = this.isConnected();
    const durationMs = this.connectedDurationMs();
    this.manualClose = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    try {
      if (this.isConnected()) await this.requestCamera(false, { timeoutMs: 3000 });
    } catch { /* transport cleanup still proceeds */ }
    this.cleanupTransport(new DOMException('Connection manually disconnected', 'AbortError'));
    this.update({ state: 'idle', lastError: reason, remoteStream: null, connectedAt: null });
    if (wasConnected && config) {
      this.reportWebRtc('webrtc.disconnected', config, {
        durationMs,
        errorCode: 'MANUAL_DISCONNECT',
      });
    }
  }

  dispose() {
    this.disposed = true;
    this.manualClose = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.cleanupTransport(new DOMException('Connection session disposed', 'AbortError'));
    this.rpc.dispose();
    this.listeners.clear();
  }

  private handleUnexpectedClose(message: string, errorCode: string) {
    if (this.manualClose || this.disposed) return;
    const config = this.config;
    const durationMs = this.connectedDurationMs();
    this.cleanupTransport();
    this.update({ state: 'reconnecting', lastError: message, remoteStream: null, connectedAt: null });
    if (config) this.reportWebRtc('webrtc.disconnected', config, { durationMs, errorCode });
    this.scheduleReconnect();
  }

  private scheduleReconnect() {
    if (this.disposed || this.manualClose || this.reconnectTimer !== null || !this.config) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect().catch(() => undefined);
    }, RECONNECT_DELAY_MS);
  }

  private scheduleDisconnectGrace(
    peer: RTCPeerConnection,
    generation: number,
    onExpired: () => void,
  ) {
    if (this.disconnectGraceTimer !== null) return;
    this.disconnectGraceTimer = setTimeout(() => {
      this.disconnectGraceTimer = null;
      if (
        this.disposed
        || this.manualClose
        || generation !== this.snapshot.generation
        || this.peer !== peer
        || peer.connectionState !== 'disconnected'
      ) return;
      onExpired();
    }, DISCONNECT_GRACE_MS);
  }

  private clearDisconnectGrace() {
    if (this.disconnectGraceTimer !== null) clearTimeout(this.disconnectGraceTimer);
    this.disconnectGraceTimer = null;
  }

  private cleanupTransport(cancelError: Error = new DOMException('Connection transport closed', 'AbortError')) {
    this.clearDisconnectGrace();
    this.activeConnect?.cancel(cancelError);
    this.signaling?.removeAllListeners();
    this.signaling?.close();
    this.signaling = null;
    const channel = this.dataChannel;
    this.dataChannel = null;
    if (channel) {
      channel.onopen = null;
      channel.onmessage = null;
      channel.onerror = null;
      channel.onclose = null;
      if (channel.readyState !== 'closed') channel.close();
    }
    const peer = this.peer;
    this.peer = null;
    if (peer) {
      peer.onicecandidate = null;
      peer.ontrack = null;
      peer.onconnectionstatechange = null;
      peer.getReceivers().forEach((receiver) => receiver.track?.stop());
      peer.close();
    }
    this.rpc.setDataChannel(null, this.snapshot.generation);
  }

  private createConnectAttempt(generation: number): ActiveConnectAttempt {
    const controller = new AbortController();
    let settled = false;
    let rejectCancellation!: (error: Error) => void;
    const cancellation = new Promise<never>((_resolve, reject) => {
      rejectCancellation = reject;
    });
    const timer = setTimeout(() => {
      cancel(new Error('WebRTC connection timed out'));
    }, CONNECT_TIMEOUT_MS);
    const cancel = (error: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      controller.abort(error);
      rejectCancellation(error);
    };
    const complete = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
    };
    return { generation, signal: controller.signal, cancellation, cancel, complete };
  }

  private requireConfig() {
    if (!this.config) throw new Error(`Robot ${this.robotId} has no connection config`);
    return this.config;
  }

  private assertCurrentGeneration(generation: number, signal?: AbortSignal) {
    if (this.disposed || signal?.aborted || generation !== this.snapshot.generation) {
      throw new DOMException('Stale connection generation', 'AbortError');
    }
  }

  private connectedDurationMs(): number | undefined {
    if (!this.snapshot.connectedAt) return undefined;
    const connectedAt = Date.parse(this.snapshot.connectedAt);
    return Number.isFinite(connectedAt) ? Date.now() - connectedAt : undefined;
  }

  private reportWebRtc(
    eventType: Extract<TelemetryEventType, `webrtc.${string}`>,
    config: RobotConnectionConfig,
    details: { durationMs?: number; errorCode?: string },
  ) {
    reportTelemetrySafely(this.telemetryReporter, {
      eventType,
      robotId: this.robotId,
      connectionSessionId: config.connectionSessionId,
      requestId: config.connectionRequestId,
      durationMs: details.durationMs,
      errorCode: details.errorCode,
    });
  }

  private update(patch: Partial<RobotConnectionSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }
}
